
import { Vue, Component, Prop } from "vue-property-decorator";

import UPDATE_PROJECT_BASIC from "@/apollo/projects/UpdateProjectBasic.gql";
import GET_PROJECT_WITH_COUNTED_PARTS from "@/apollo/projects/GetProjectWithCountedParts.gql";
import GET_PROJECT_TAB_COUNTERS from "@/apollo/projects/GetProjectTabCounters.gql";
import GET_DOCUMENTS_COUNT from "@/apollo/documents/GetDocumentsCount.gql";
import ProjectHeader from "@/components/projects/ProjectHeaderNew.vue";
import ProjectModal from "@/components/companyProjects/ProjectModalNew.vue";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import DollarIcon from "@/components/icons/DollarIcon.vue";
import PageHeadIcon from "@/components/page/PageHeadIcon.vue";
import TabsWithRoutes from "@/components/common/TabsWithRoutesNew.vue";
import NavGrayArrow from "@/components/icons//NavGrayArrow.vue";
import ActivityHoursUnconfirmedAlert from "@/components/projects/activities/ActivityHoursUnconfirmedAlert.vue";
import ProjectDocumentsSideBar from "@/components/contractors/ProjectDocumentsSideBar.vue";
import {
  Company,
  Contractor,
  Project,
  Project_Skill,
  User,
} from "@/generated/graphql";
import RouteNames from "@/entities/RouteNames";
import RouteTabs from "@/entities/RouteTabs";
import { NOTIFICATION } from "@/entities/Events";
import Message from "@/data/notificationsMessages";
import ProjectCustomRequest from "@/components/projects/ProjectCustomRequest.vue";
import { getProjectCustomRequestConditions } from "@/helpers/queryConditions/customRequests";
import TabNotificationBubble from "@/components/common/TabNotificationBubbleNew.vue";
import ExtendedTab from "@/components/common/ExtendedTab.vue";
import UPDATE_MULTIPLE_PROJECT_ACTIVITIES from "@/apollo/projectActivities/UpdateMultipleVisits.gql";
import Roles from "@/entities/Roles";
import ClientEditModal from "@/components/companyProjects/ClientEditModal.vue";
import GET_PROJECT_CLIENT from "@/apollo/projectActivities/GetProjectClient.gql";
import GET_PROJECT_CARE_PLAN from "@/apollo/projectActivities/GetProjectCarePlan.gql";

interface ProjectData {
  project?: Project;
  activitiesCount: number;
  documentsCount: number;
  contractorsCount: number;
  emptyactivitiesCount: number;
  requestsCount: number;
  groupsCount: number;
  notesCount: number;
}

@Component({
  components: {
    ExtendedTab,
    TabNotificationBubble,
    ProjectCustomRequest,
    ProjectHeader,
    ProjectModal,
    NavGrayArrow,
    TabsWithRoutes,
    ProjectDocumentsSideBar,
    LoadingOverlay,
    PageHeadIcon,
    DollarIcon,
    ActivityHoursUnconfirmedAlert,
    ClientEditModal,
  },
  apollo: {
    projectData: {
      query: GET_PROJECT_WITH_COUNTED_PARTS,
      variables() {
        const projectId = this.$route.params.id;
        return {
          id: projectId,
          requestsCountConditions: getProjectCustomRequestConditions(projectId),
        };
      },
      update: ({
        project_by_pk,
        activities_count,
        empty_activities_count,
        documents_count,
        contractors_count,
        requests_count,
        groups_count,
        staff_notes_count,
      }) => ({
        project: project_by_pk,
        activitiesCount: activities_count.aggregate.count,
        documentsCount: documents_count.aggregate.count,
        contractorsCount: contractors_count.aggregate.count,
        emptyactivitiesCount: empty_activities_count.aggregate.count,
        requestsCount: requests_count.aggregate.count,
        groupsCount: groups_count.aggregate.count,
        notesCount: staff_notes_count?.aggregate?.count,
      }),
    },
    tabCounters: {
      query: GET_PROJECT_TAB_COUNTERS,
      variables() {
        const projectId = this.$route.params.id;
        return {
          id: projectId,
          requestsCountConditions: getProjectCustomRequestConditions(projectId),
        };
      },
      skip() {
        return this.tabCountersQuerySkipped;
      },
      update({
        activities_count,
        documents_count,
        empty_activities_count,
        contractors_count,
        requests_count,
        groups_count,
        staff_notes_count,
      }) {
        this.projectData = {
          ...this.projectData,
          activitiesCount: activities_count.aggregate.count,
          emptyActivitiesCount: empty_activities_count.aggregate.count,
          documentsCount: documents_count.aggregate.count,
          contractorsCount: contractors_count.aggregate.count,
          requestsCount: requests_count.aggregate.count,
          groupsCount: groups_count.aggregate.count,
          notesCount: staff_notes_count?.aggregate?.count,
        };
      },
    },
  },
})
export default class ProjectPage extends Vue {
  @Prop({ type: Object, required: true }) readonly company!: Company;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;

  routes: typeof RouteNames = RouteNames;
  processing: boolean = false;
  dialog: boolean = false;
  tabCountersQuerySkipped = true;
  activeTab = "basic";
  currentEditTab = { tab: 0, step: 0 };
  initialProject: Project = undefined;
  isLoading: boolean = true;
  isCareplanExist: boolean = false;

  projectData: ProjectData = {
    activitiesCount: 0,
    documentsCount: 0,
    contractorsCount: 0,
    requestsCount: 0,
    groupsCount: 0,
    emptyactivitiesCount: 0,
    notesCount: 0,
    project: this.initialProject,
  };

  get isAdmin(): boolean {
    return this.currentUser.role === Roles.ADMIN;
  }

  handleClose(): void {
    this.dialog = false;
    // window.location.reload();
  }
  hanldeCancel(): void {
    this.dialog = false;
  }
  get requestsBubbleShown(): boolean {
    return this.isAdmin && this.requestsExist;
  }

  get projectPageRoute() {
    return {
      name: RouteNames.ALL_CLIENTS,
    };
  }
  async created() {
    await this.fetchData();
  }
  async fetchData() {
    try {
      // Fetch project data
      const projectQuery = this.$apollo.query({
        query: GET_PROJECT_CLIENT,
        variables: {
          where: { id: { _eq: this.$route.params.id } },
        },
      });

      // Fetch care plan data
      const carePlanQuery = this.$apollo.query({
        query: GET_PROJECT_CARE_PLAN,
        variables: {
          project_id: this.$route.params.id,
        },
      });

      // Wait for both queries to complete
      const [projectResult, carePlanResult] = await Promise.all([
        projectQuery,
        carePlanQuery,
      ]);

      // Check if care plan exists
      this.isCareplanExist =
        carePlanResult.data.client_care_plan &&
        carePlanResult.data.client_care_plan.length > 0;

      // Extract data from results
      const projectData = projectResult.data.project[0] || {};
      const carePlanData = carePlanResult.data.client_care_plan[0] || {};

      // Merge data only when both are available
      this.initialProject = this.customMerge(projectData, carePlanData);
    } catch (error) {
      console.error("Error fetching data:", error);
      this.$eventBus.emit(
        NOTIFICATION,
        Message.errorMessage("Failed to fetch data.")
      );
    } finally {
      this.isLoading = false; // Set loading to false
    }
  }
  customMerge(projectData, carePlanData) {
    const mergedData = { ...projectData }; // Start with project data

    // Iterate over carePlanData and add only non-overlapping fields
    for (const key in carePlanData) {
      if (!(key in projectData)) {
        mergedData[key] = carePlanData[key];
      }
    }

    return mergedData;
  }

  get dashboardRoute() {
    return {
      name: RouteNames.DASHBOARD,
    };
  }
  get requestsExist(): boolean {
    return this.projectData.requestsCount > 0;
  }

  get requestTabText(): string {
    if (!this.requestsExist) {
      return `Requests`;
    }

    return `Requests (${this.projectData.requestsCount})`;
  }

  get routeTabs(): RouteTabs {
    const timeTrackerRoute = () => {
      // if(this.isAdmin) {
      return `Logged hours`;
      // }
      // return "";
    };
    return {
      [RouteNames.PROJECT_ACTIVITY]: `Visits (${this.projectData.activitiesCount})`,
      [RouteNames.PROJECT_ACTIVITY_EMPTY]: `Open Visits (${this.projectData.emptyactivitiesCount})`,
      [RouteNames.PROJECT_DETAILS]: "details",
      [RouteNames.PROJECT_CLIENT]: `Client`,
      [RouteNames.PROJECT_CARE_PLAN]: `Care Plan`,
      [RouteNames.PROJECT_CONTRACTORS]: `Contractors (${this.projectData.contractorsCount})`,
      [RouteNames.PROJECT_NOTES]: `Notes (${this.projectData.notesCount})`,
      [RouteNames.PROJECT_REQUESTS]: this.requestTabText,
      [RouteNames.PROJECT_GROUPS]: `groups (${this.projectData.groupsCount})`,
      [RouteNames.PROJECT_TIME_TRACKED]: timeTrackerRoute(),
      [RouteNames.PROJECT_DOCUMENTS]: `documents (${this.projectData.documentsCount})`,
    };
  }

  get hasUnconfirmedHours(): boolean {
    if (!this.project?.project_activities) {
      return false;
    }

    const confirmed = this.project.project_activities.reduce<boolean>(
      (confirmed, activity) => {
        const { requested_hours, confirmed_hours } = activity;
        const activityConfirmed = !requested_hours || Boolean(confirmed_hours);
        return confirmed && activityConfirmed;
      },
      true
    );

    return !confirmed;
  }

  get project(): Project | undefined {
    if (!this.projectData.project) {
      return undefined;
    }
    const breadcrumbdoc = document.querySelector(".not_link_breadcrumb");
    if (breadcrumbdoc) {
      breadcrumbdoc.innerHTML = this.projectData.project.client_name;
    }
    return this.projectData.project;
  }

  get projectSkills(): Project_Skill[] {
    return this.project?.project_skills || [];
  }

  get projectId(): string {
    return this.project?.id;
  }

  get projectCompleted(): boolean {
    return Boolean(this.project?.completed);
  }

  get projectDescription(): string {
    return this.project?.description!;
  }

  async updateDocumentsCount(): Promise<void> {
    const { data } = await this.$apollo.query({
      query: GET_DOCUMENTS_COUNT,
      variables: {
        id: this.$route.params.id,
      },
      fetchPolicy: "no-cache",
    });

    this.projectData.documentsCount = data.documents_count.aggregate.count;
  }

  refetchProject(): void {
    this.$apollo.queries.projectData.refetch();
  }

  enableEditMode(): void {
    this.dialog = true;
  }

  disableEditMode(): void {
    this.dialog = false;
  }

  message(item: Contractor): void {
    this.$router.push({
      name: "inbox",
      query: { recipientUserId: item.user.id },
    });
  }

  async updateProjectStatus({
    completed,
    active,
    date_completed,
  }: {
    completed: boolean;
    active: boolean;
    date_completed: string;
  }): Promise<void> {
    this.processing = true;

    if (!this.project) {
      return;
    }

    try {
      const { data } = await this.$apollo.mutate({
        mutation: UPDATE_PROJECT_BASIC,
        variables: {
          set: { completed, active, date_completed },
          id: this.project.id,
        },
      });

      this.projectData = {
        ...this.projectData,
        project: data.update_project_by_pk,
      };

      if (
        this.projectData.project &&
        Array.isArray(this.projectData.project.project_activities)
      ) {
        const allActivityIds = this.projectData.project.project_activities.map(
          (item) => item.id
        );

        if (allActivityIds.length) {
          await this.updateMultipleActivities({
            ids: allActivityIds,
            set: { completed: true, drafted: false },
          });
        }
      }

      this.$eventBus.emit(
        NOTIFICATION,
        Message.successMessage("Client and visits updated successfully.")
      );
      window.location.reload();
    } catch (e) {
      this.$eventBus.emit(NOTIFICATION, Message.errorMessage(e.message));
    } finally {
      this.processing = false;
    }
  }

  async updateMultipleActivities({
    ids,
    set,
  }: {
    ids: string[];
    set: Record<string, boolean>;
  }) {
    try {
      return await this.$apollo.mutate({
        mutation: UPDATE_MULTIPLE_PROJECT_ACTIVITIES,
        variables: {
          ids,
          set,
        },
      });
    } catch (error) {
      console.error("Error updating multiple activities:", error);
      throw error;
    }
  }

  refetchTabData(): void {
    if (this.tabCountersQuerySkipped) {
      this.tabCountersQuerySkipped = false;
    }
    sessionStorage.removeItem("isGroups");

    this.$apollo.queries.tabCounters.refetch();
  }


  handleViewAllClick(): void {
    this.$nextTick(() => {
      const el = document.getElementById('content-scroll-manager');
      el.scrollTo({
        top: el.scrollHeight,
        behavior: 'smooth'
      });
    });

  }
}
